import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../shared/api/client";
import type {
  EmployeeSummary,
  PageResponse,
  ReleaseApproval,
  ReleaseDetail,
} from "../../shared/types/api";
import { Button, Drawer, Field, InlineAlert, Select } from "../../shared/ui";
import { ConflictNotice } from "./ConflictNotice";
import { useReleaseMutation } from "./useReleaseMutation";

/**
 * Names the person being asked to approve (§31).
 *
 * <p>This is what makes an approval accountable rather than procedural. Until a seat has a name
 * on it, anybody holding {@code RELEASE_APPROVE} can answer it — and an approval given by whoever
 * happened to have the permission is not the sign-off §31 is asking for. Once somebody is named,
 * the service refuses a decision from anybody else.
 */
export function AssignApproverDrawer({
  release,
  approval,
  onClose,
  onSaved,
}: {
  release: ReleaseDetail;
  approval: ReleaseApproval | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [approverId, setApproverId] = useState("");
  const { run, error, conflict, saving, reset } = useReleaseMutation(release.id);

  const employees = useQuery({
    queryKey: ["employees", "picker"],
    queryFn: () =>
      api.get<PageResponse<EmployeeSummary>>(
        "/api/v1/employees?active=true&size=200&sort=fullName,asc",
      ),
    enabled: approval != null,
  });

  useEffect(() => {
    if (!approval) {
      return;
    }
    setApproverId(approval.approver?.id ?? "");
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approval]);

  const submit = () =>
    void run(
      () =>
        api.put(`/api/v1/releases/${release.id}/approvals/${approval?.id}/approver`, {
          approverId: approverId === "" ? null : approverId,
          lockVersion: approval?.lockVersion,
        }),
      () => {
        onSaved();
        onClose();
      },
    );

  return (
    <Drawer
      open={approval != null}
      onClose={onClose}
      title="Who is being asked?"
      subtitle={approval?.roleLabel}
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} loading={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {conflict ? <ConflictNotice onRefresh={onSaved} /> : error && (
          <InlineAlert tone="error">{error}</InlineAlert>
        )}

        <Field
          label="Approver"
          hint="Once named, only this person can answer — which is the point of naming them."
        >
          <Select value={approverId} onChange={(event) => setApproverId(event.target.value)}>
            <option value="">Nobody named yet</option>
            {(employees.data?.content ?? []).map((employee) => (
              <option key={employee.id} value={employee.id}>
                {employee.fullName}
              </option>
            ))}
          </Select>
        </Field>
      </div>
    </Drawer>
  );
}
